import { controlPointLabel, patientControlPoints } from "./controleVariants";
import { formatPatientName } from "./controleFormat";
import {
    PATIENT_CONTROL_POINT_KEYS,
    PATIENT_SHARED_COMMENT_KEY,
    PATIENT_VALIDATION_KEY,
    isControlePatientActive,
    isPatientControlValidated,
    normalizePatientPointState,
    patientControlSortRank,
    sharedPatientControlComment,
} from "./patientControlRules";

// Mises à jour immuables des contrôles patients enregistrés dans les données d'un dossier de lot.

function withPatientControls(data, patientId, update) {
    const controls = data.controlePatients || {};
    return {
        ...data,
        controlePatients: {
            ...controls,
            [patientId]: update(controls[patientId] || {}),
        },
    };
}

export function withPatientPoint(data, patientId, pointKey, patch) {
    return withPatientControls(data, patientId, current => ({
        ...current,
        [pointKey]: { ...normalizePatientPointState(current[pointKey]), ...patch },
    }));
}

export function withPatientValidation(data, patientId, validated) {
    return withPatientControls(data, patientId, current => ({ ...current, [PATIENT_VALIDATION_KEY]: validated }));
}

function withEveryPoint(current, patch) {
    return {
        ...current,
        ...Object.fromEntries(PATIENT_CONTROL_POINT_KEYS.map(pointKey => [
            pointKey,
            { ...normalizePatientPointState(current[pointKey]), ...patch },
        ])),
    };
}

export function withAllPointsValidated(data, patientId) {
    return withPatientControls(data, patientId, current => withEveryPoint(current, { validated: true }));
}

export function withPatientSharedComment(data, patientId, comment) {
    return withPatientControls(data, patientId, current => ({
        ...withEveryPoint(current, { comment }),
        [PATIENT_SHARED_COMMENT_KEY]: comment,
    }));
}

export function launchStatusPatch(launchStatus, user, now = new Date()) {
    const launched = launchStatus === "LANCE";
    return {
        launchStatus,
        launchStatusAt: launched ? now.toISOString() : "",
        launchStatusBy: launched ? (user?.username || user?.email || "") : "",
    };
}

// Points restés ouverts chez les patients actifs déjà validés : ils alimentent le rapport « à reprendre ».
export function findBlockingPatientIssues({ patients, controls, variant, controleMode, useSharedComment = false }) {
    return (patients || []).flatMap(patient => {
        if (!isControlePatientActive(patient)) return [];
        const patientControls = (controls || {})[patient.patientId] || {};
        if (!isPatientControlValidated(patientControls)) return [];
        return patientControlPoints(variant).flatMap(([pointKey, label], index) => {
            const state = normalizePatientPointState(patientControls[pointKey]);
            if (state.validated) return [];
            return [{
                patientId: patient.patientId,
                patientName: formatPatientName(patient),
                room: patient.chambre || "",
                pointKey,
                pointNumber: index + 2,
                pointLabel: controlPointLabel(variant, "avantLancement", pointKey, label, controleMode),
                comment: useSharedComment ? sharedPatientControlComment(patientControls) : state.comment,
                launchStatus: pointKey === "tracageLogiciels" ? (state.launchStatus || "PAS_LANCE") : "",
            }];
        });
    });
}

export function countPatients(patients, controls) {
    const activePatients = (patients || []).filter(isControlePatientActive);
    const validated = activePatients.filter(patient => isPatientControlValidated((controls || {})[patient.patientId] || {})).length;
    return { active: activePatients.length, validated, remaining: Math.max(activePatients.length - validated, 0) };
}

export function sortPatientsForControl(patients, controls) {
    return [...(patients || [])].sort((a, b) => {
        const aRank = patientControlSortRank(a, (controls || {})[a.patientId] || {});
        const bRank = patientControlSortRank(b, (controls || {})[b.patientId] || {});
        if (aRank !== bRank) return aRank - bRank;
        return formatPatientName(a).localeCompare(formatPatientName(b), "fr", { sensitivity: "base" });
    });
}
